import { AppRoutes } from 'config/routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import axios from 'axios';
import { useGetMyOrganizationParticipationsQuery } from 'store/api/organizations';
import { generateValidationSchema, parseScheme, parseSteps } from 'utils/sheme-parser-v2';
import { getToken } from 'utils/token';
import { getAcceptLanguage } from 'utils/locale';
import { findApiUrlByFieldPath } from '../all/List';
import {
  useGetCoreDocumentsNoCacheMutation,
  useSignDocumentMutation,
} from 'store/api/documents';
import { useHandleMutation } from 'utils/handleMutation';

export const useNewRequest = () => {
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const { id: documentId } = useParams();
  const { data: widgets } = useGetMyOrganizationParticipationsQuery();

  const [fetchDocument] = useGetCoreDocumentsNoCacheMutation({});

  const [signDocument, signDocumentMutation] = useSignDocumentMutation();

  const [step, setStep] = useState<number>(1);
  const [currentScheme, setCurrentScheme] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { Home } = AppRoutes.Dashboard;
  const urlParams = new URLSearchParams(window.location.search);
  const name = urlParams.get('name');

  const apiUrl = findApiUrlByFieldPath(name ?? '', widgets);

  const fetchScheme = async () => {
    try {
      if (!apiUrl) {
        console.error('API URL not found');
        return;
      }

      const url = documentId?.length ? `${apiUrl}/${documentId}/update` : `${apiUrl}/create`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Accept-Language': getAcceptLanguage(),
        },
      });

      setCurrentScheme(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleClose = useCallback(() => {
    navigate(Home + AppRoutes.Dashboard.CustomWidget.Home + `?name=${name}`);
  }, []);

  const handleSign = useCallback(
    async (id?: string, version?: number) => {
      setIsLoading(true);

      const { data } = (await fetchDocument({ ids: [id ?? ''] })) as any;

      const [document] = data ?? [];

      await signDocument({
        signature: null,
        detachedSignature: null,
        documentId: id ?? document?.id ?? '',
        documentVersion: version ?? document?.version ?? 0,
      });
    },

    [fetchDocument, signDocument],
  );

  const onSuccessSign = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 5000));
    setIsLoading(false);
    handleClose();
  }, [handleClose]);

  const onErrorSign = useCallback((err: any) => {
    console.error('Error sign', err);
    setIsLoading(false);
  }, []);

  const handleCreate = async (values: any) => {
    try {
      setIsLoading(true);
      if (!apiUrl) {
        console.error('API URL not found');
        return;
      }

      const response = await axios.post(
        `${apiUrl}`,
        {
          data: values,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Accept-Language': getAcceptLanguage(),
          },
        },
      );

      await handleSign(response.data?.id);
    } catch (error) {
      console.error('Error creating document:', error);
      setIsLoading(false);
    }
  };

  useHandleMutation({
    ...signDocumentMutation,
    onSuccess: onSuccessSign,
    onError: onErrorSign,
  });

  const getValidationSchema = () => {
    if (isApproval) {
      return Yup.object().shape({});
    }
    return generateValidationSchema(currentScheme, step);
  };

  const isApproval = path.includes(AppRoutes.Dashboard.Contracts.ContractApproval);

  const { buttons, model, steps } = useMemo(() => {
    return {
      buttons: parseScheme(currentScheme)?.buttons,
      model: parseScheme(currentScheme)?.model,
      steps: parseSteps(currentScheme),
    };
  }, [currentScheme]);

  useEffect(() => {
    fetchScheme();
  }, [apiUrl]);

  return {
    step,
    steps,
    model,
    setStep,
    handleCreate,
    handleSign,
    isApproval,
    handleClose,
    currentScheme,
    getValidationSchema,
    buttons,
    isLoading,
  };
};
